import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { LocationSelect, Select } from '../../../../components/Select'
import { Button } from '../../../../components/Button'
import { Input } from '../../../../components'
import { UserAvatar } from '../../../../components/UserAvatar'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/Forsm/form'
import { teamFormSchema, type CreateEquipeRequest, type TeamFormValues } from './schemas'
import type { User } from '../../../../models'
import { FaUsers, FaEnvelope, FaIdBadge, FaStar, FaGlobeAmericas, FaMapMarkerAlt } from 'react-icons/fa'
import { useCidades } from '../../hooks'

interface TeamFormProps {
  usuarios: User[]
  onSubmit: (data: CreateEquipeRequest) => Promise<void>
  isLoading?: boolean
  initialData?: {
    id?: number
    nome_setor?: string
    chefe_setor_fk?: number
    cidade_fk?: number
  } | undefined
  isEdit?: boolean
}

export const TeamForm = ({
  usuarios,
  onSubmit,
  isLoading = false,
  initialData,
  isEdit = false,
}: TeamFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
   const { data: cidades, isLoading: isLoadingCidades } = useCidades()

  const form = useForm({
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      nome_setor: initialData?.nome_setor || '',
      chefe_setor_fk: initialData?.chefe_setor_fk?.toString() || '',
      cidade_fk: initialData?.cidade_fk?.toString() || "",
    },
  })

    const cidadeOptions = useMemo(() => {
    if (!cidades) return []
    return cidades.map(cidade => ({
      value: cidade.id.toString(),
      label: `${cidade.name} - ${cidade.estado?.name || ''}, ${cidade.estado?.pais?.name || ''}`,
      cidade: cidade.name,
      ...(cidade.estado?.name && { estado: cidade.estado.name }),
      ...(cidade.estado?.pais?.name && { pais: cidade.estado.pais.name }),
    }))
  }, [cidades])

  // Converter usuários para opções do Select (com formato UserOption)
  const userOptions = usuarios.map(user => ({
    value: user.id.toString(),
    label: user.name,
    name: user.name,
    email: user.email,
    ...(user.avatar && { avatar: user.avatar }),
  }))

    // Encontrar cidade selecionada
  const selectedCidadeId = form.watch("cidade_fk")
  const selectedCidade = useMemo(() => {
    if (!selectedCidadeId || !cidades) return null
    return cidades.find(c => c.id.toString() === selectedCidadeId) || null
  }, [selectedCidadeId, cidades])

  // Encontrar usuário selecionado
  const selectedUserId = form.watch('chefe_setor_fk')
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null
    return usuarios.find(user => user.id.toString() === selectedUserId) || null
  }, [selectedUserId, usuarios])

  const handleSubmit = async (data: TeamFormValues) => {
    try {
      setIsSubmitting(true)

      // Converter para o formato da API
      const requestData: CreateEquipeRequest = {
        nome_setor: data.nome_setor,
        cidade_fk: Number(data.cidade_fk),
        ...(data.chefe_setor_fk &&
          data.chefe_setor_fk !== '' && {
            chefe_setor_fk: Number(data.chefe_setor_fk),
          }),
      }

      await onSubmit(requestData)

      // Resetar formulário após sucesso
      form.reset()
      navigate('/gestao/equipes')
    } catch (error) {
      console.error('Erro ao criar equipe:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 h-[calc(100vh-206px)]'>
        {/* Primeira Coluna - Formulário */}
        <div className='px-6 py-8 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent'>
          <div className='mb-8'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3'>
              <FaUsers className='text-green-600 dark:text-green-400' />
              {isEdit ? 'Editar Equipe' : 'Criar Nova Equipe'}
            </h2>
            <p className='mt-2 text-gray-600 dark:text-gray-400'>
              {isEdit
                ? 'Modifique as informações da equipe conforme necessário.'
                : 'Defina uma nova equipe de trabalho com seu respectivo líder.'}
            </p>
          </div>

          <div className='space-y-6'>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
                {/* Nome da Equipe */}
                <FormField
                  control={form.control}
                  name='nome_setor'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Equipe</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ex: Equipe de Desenvolvimento'
                          {...field}
                          className='text-base'
                        />
                      </FormControl>
                      <FormDescription>Nome identificador da equipe de trabalho</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Localização - Cidade */}
                <FormField
                  control={form.control}
                  name='cidade_fk'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Localização</FormLabel>
                      <FormControl>
                        <LocationSelect
                          options={cidadeOptions}
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder='Selecione a cidade...'
                          searchable
                          disabled={isLoading || isLoadingCidades}
                        />
                      </FormControl>
                      <FormDescription>Cidade onde o setor está localizado</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Líder da Equipe */}
                <FormField
                  control={form.control}
                  name='chefe_setor_fk'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Líder da Equipe</FormLabel>
                      <FormControl>
                        <Select
                          variant='user'
                          options={userOptions}
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder='Selecione o líder da equipe...'
                          searchable
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Usuário responsável pela liderança da equipe
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Botões de ação */}
                <div className='pt-6 border-t border-gray-200 dark:border-neutral-700'>
                  <div className='flex flex-col sm:flex-row gap-3 sm:justify-end'>
                    <Button
                      type='button'
                      variant='outline'
                      className='order-2 sm:order-1'
                      onClick={() => form.reset()}
                      disabled={isSubmitting || isLoading}
                    >
                      Limpar
                    </Button>
                    <Button
                      type='submit'
                      className='order-1 sm:order-2'
                      disabled={isSubmitting || isLoading}
                    >
                      {isSubmitting
                        ? isEdit
                          ? 'Salvando...'
                          : 'Criando...'
                        : isEdit
                          ? 'Salvar Alterações'
                          : 'Criar Equipe'}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>

        {/* Segunda Coluna - Informações do Líder */}
        <div className='px-6 py-8 bg-gradient-to-br from-green-50 dark:from-green-900/20 to-emerald-50 dark:to-emerald-900/20 border-l border-green-100 dark:border-green-800 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent'>
          <div className='space-y-6'>
            {/* Informações de Localização */}
            <div>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2'>
                <FaGlobeAmericas className='text-green-600 dark:text-green-400' />
                Localização da Equipe
              </h3>
              
              <div className='bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-4'>
                {selectedCidade ? (
                  <div className='flex items-center gap-3'>
                    <FaMapMarkerAlt className='text-green-500 flex-shrink-0 w-5 h-5' />
                    <div>
                      <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                        {selectedCidade.name} - {selectedCidade.estado?.name}, {selectedCidade.estado?.pais?.name}
                      </p>
                      <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                        Localização da equipe
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-4'>
                    <FaGlobeAmericas className='w-8 h-8 text-gray-400 mx-auto mb-2' />
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                      Selecione a localização da equipe
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Informações do Líder */}
            <div>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2'>
                <FaStar className='text-green-600 dark:text-green-400' />
                Informações do Líder
              </h3>
            
            <AnimatePresence mode='wait'>
              {selectedUser ? (
                <motion.div
                  key={selectedUser.id}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                  }}
                  className='bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6 text-center space-y-4'
                >
                  {/* Avatar centralizado */}
                  <motion.div
                    className='flex justify-center'
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    <div className='relative'>
                      <UserAvatar
                        user={selectedUser}
                        size='xl'
                        showPopup={false}
                        className='shadow-lg border-4 border-white'
                      />
                      <motion.div
                        className='absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center'
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.2, type: 'spring', stiffness: 200 }}
                      >
                        <FaStar className='w-3 h-3' />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Nome e título centralizados */}
                  <motion.div
                    className='space-y-2'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.3 }}
                  >
                    <h4 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                      {selectedUser.name}
                    </h4>
                    <p className='text-green-600 dark:text-green-400 font-semibold'>
                      Líder da Equipe
                    </p>
                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                      {selectedUser.cargo_usuario}
                    </p>
                  </motion.div>

                  {/* Informações organizadas */}
                  <motion.div
                    className='space-y-3 pt-4 border-t border-gray-100 dark:border-neutral-700'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <div className='flex items-center justify-center gap-2 text-sm'>
                      <FaEnvelope className='text-gray-400 w-4 h-4' />
                      <span className='text-gray-600 dark:text-gray-400'>{selectedUser.email}</span>
                    </div>

                    <div className='flex justify-center'>
                      <span className='inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-sm font-medium rounded-full'>
                        <FaIdBadge className='w-3 h-3' />
                        {selectedUser.funcao_usuario}
                      </span>
                    </div>
                  </motion.div>

                  {/* Descrição */}
                  <motion.div
                    className='bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mt-4'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.3 }}
                  >
                    <p className='text-sm text-green-800 dark:text-green-300 text-center'>
                      <strong>Este usuário será designado como líder da equipe</strong>
                      <br />
                      <span className='text-green-700 dark:text-green-400'>
                        Responsável por coordenar e orientar os membros da equipe.
                      </span>
                    </p>
                  </motion.div>
                </motion.div>
              ) : (
                <div className='text-center py-8'>
                  <div className='bg-gray-100 dark:bg-neutral-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4'>
                    <FaStar className='w-8 h-8 text-gray-400' />
                  </div>
                  <p className='text-gray-500 dark:text-gray-400 text-sm'>
                    Selecione um usuário para ver as informações do líder da equipe
                  </p>
                </div>
              )}
            </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
